<template>
  <component
    :is="tag"
    :class="[
      // Base
      'v-card',

      // Background variant
      overlay && bgVariantOutput.class && `bg-${bgVariantOutput.class}`,

      // Overlay
      {
        'v-card--overlay': overlay
      },

      // Elevated
      {
        'v-card--elevated': elevated
      },

      // Animation
      {
        'v-card--animate-from': animation.enabled,
        'v-card--animate-to': animation.started
      },

      // Image position
      {
        // Small breakpoint
        'flex-column': !imgPosition || imgPosition === 'top' && !noSplit,
        'flex-row': imgPosition === 'left',
        'flex-row-reverse': imgPosition === 'right' || noSplit,

        // Medium breakpoint
        'flex-md-column': imgMdPosition === 'top',
        'flex-md-row': imgMdPosition === 'left',
        'flex-md-row-reverse': imgMdPosition === 'right',

        // Large breakpoint
        'flex-lg-column': imgLgPosition === 'top',
        'flex-lg-row': imgLgPosition === 'left',
        'flex-lg-row-reverse': imgLgPosition === 'right'
      }
    ]"
    :style="[animation.enabled && {transitionDelay: animation.delay}]"
    v-on="$listeners"
  >
    <!-- Waypoint for image lazy loading -->
    <div
      v-if="!!imgSrc"
      v-waypoint="{ active: !seen, callback: onWaypoint, options: intersectionOptions }"
    ></div>

    <!-- Overlay -->
    <div
      v-if="overlay"
      :class="[
        'v-card__overlay',
        imgLoaded && 'v-card__overlay--show',
        { 'v-card__overlay--show--noSplit': noSplit }
      ]"
      :style="{
        backgroundImage: !!imgSrc && imgLoaded && `url('${imgSrc}')`,
        backgroundPosition: !!imgAlign && imgAlign
      }"
    >
      <div
        v-if="!!imgSrc"
        :class="[
          'v-card__overlay__color-1',
          bgVariantOutput.class && `text-${bgVariantOutput.class}`
        ]"
        :style="{
          color: bgVariantOutput.color,
          backgroundImage: !!imgSrc && `linear-gradient(90deg, currentColor, currentColor 5%, rgba(${bgVariantOutput.gradientRgb},0))`,
          backgroundPosition: !!imgAlign && imgAlign
        }"
      ></div>
      <div
        v-if="!!imgSrc"
        :class="[
          'v-card__overlay__color-2',
          bgVariantOutput.class && `text-${bgVariantOutput.class}`
        ]"
        :style="{
          color: bgVariantOutput.color,
          backgroundImage: !!imgSrc && `linear-gradient(-180deg, currentColor, currentColor 5%, rgba(${bgVariantOutput.gradientRgb},0))`,
          backgroundPosition: !!imgAlign && imgAlign
        }"
      ></div>
    </div>

    <!-- Image container -->
    <div
      v-if="!!imgSrc"
      :class="[
        'v-card__image--wrapper',
        !overlay && bgVariantOutput.class && `bg-${bgVariantOutput.class}`,
        { 'v-card__image--wrapper--noSplit': noSplit }
      ]"
    >
      <div
        :class="[
          'v-card__image',
          'v-card__image__default',
          imgLoaded && 'v-card__image--show'
        ]"
        :style="{
          backgroundImage: !overlay && !!imgSrc && imgLoaded && `url('${imgSrc}')`,
          backgroundPosition: !!imgAlign && imgAlign,
        }"
      >
        <!-- Video button overlay -->
        <div v-if="video && imgLoaded" class="v-card__image--wrapper__video-overlay" :class="{'v-card__image--wrapper__video-overlay--no-overlay': overlay}">
          <v-icon class="v-card__image--wrapper__video-overlay__icon" name="play-button" width="45"></v-icon>
        </div>
      </div>
      <div
        :class="[
          'v-card__image',
          'v-card__image__large',
          imgLoaded && 'v-card__image--show'
        ]"
        :style="{
          backgroundImage: !overlay && (!!imgMdSrc || !!imgSrc) && imgLoaded && `url('${imgMdSrc || imgSrc}')`,
          backgroundPosition: !!imgAlign && imgAlign,
        }"
      ></div>
    </div>

    <!-- Card content -->
    <div
      :class="[
        'v-card__content',
        !overlay && bgVariantOutput.class && `bg-${bgVariantOutput.class}`,
      ]"
      :style="[
        !imgSrc && { height: '100%' },
        { backgroundColor: bgVariantOutput.color }
      ]"
    >
      <div class="v-card__content__relative">
        <v-infoicon v-if="tooltip" :tooltip="tooltip" :placement="tooltipPlacement"></v-infoicon>
      </div>
      <div
        :class="[
        !!padding && padding,
        'h-100',
        'v-card__content--inner',
        { 'py-3 py-md-5': !!imgSrc && !padding }]"
      >
        <div style="display: flex; height: 100%;">
          <div style="flex: 1; height: 100%; max-width: 100%;">
            <slot></slot>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import Vue from "vue";
import VueWaypoint from "vue-waypoint";

Vue.use(VueWaypoint);

export default {
  props: {
    tag: {
      type: String,
      default: "div"
    },
    bgVariant: {
      type: String,
      default: "light"
    },
    imgSrc: {
      type: String
    },
    imgMdSrc: {
      type: String
    },
    imgPosition: {
      type: String,
      default: "top"
    },
    tooltip: {
      type: String
    },
    tooltipPlacement: {
      type: String
    },
    imgMdPosition: {
      type: String
    },
    imgLgPosition: {
      type: String
    },
    imgAlign: {
      type: String
    },
    overlay: {
      type: Boolean,
      default: false
    },
    elevated: {
      type: Boolean,
      default: false
    },
    padding: {
      type: String,
      default: null
    },
    noSplit: {
      type: Boolean,
      default: false
    },
    video: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      seen: false,
      imgLoaded: false,
      intersectionOptions: {
        root: null,
        rootMargin: "0px 0px 500px 0px",
        threshold: [0, 1]
      },
      animation: {
        enabled: false,
        started: false,
        delay: null
      }
    };
  },
  methods: {
    onWaypoint({ going }) {
      if (!this.seen && going === this.$waypointMap.GOING_IN) {
        this.loadImg();
        this.seen = true;
      }
    },
    loadImg() {
      const img = new Image();
      img.onload = () => (this.imgLoaded = true);
      img.src = this.imgSrc;
    },
    enableAnimation() {
      this.animation.enabled = true;
    },
    startAnimation(delay) {
      this.animation.delay = delay;
      this.animation.started = true;
    }
  },
  computed: {
    bgVariantOutput: function() {
      const variant = this.$props.bgVariant;

      if (!variant) {
        return "light";
      }

      const isHex = /^#[a-zA-Z0-9]{6}|#[a-zA-Z0-9]{3}$/.test(variant);

      return {
        class: !isHex && `${variant}`,
        color: isHex && variant,
        gradientRgb: variant == "dark" ? "40,40,40" : "242,242,244"
      };
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

$border-radius-top: $border-radius $border-radius 0 0;
$border-radius-bottom: 0 0 $border-radius $border-radius;
$border-radius-left: $border-radius 0 0 $border-radius;
$border-radius-right: 0 $border-radius $border-radius 0;

.v-card {
  display: flex;
  color: inherit;
  text-decoration: none !important;
  position: relative;
  background-position: center center;
  background-repeat: no-repeat;
  height: 100%;

  &__content {
    transition: background-color 250ms ease-in-out;
    flex-grow: 1;
    border-radius: $border-radius;

    &__relative {
      position: relative;
    }

    &--inner {
      padding: $spacer;

      @include media-breakpoint-up(md) {
        padding: $spacer * 1.5;
      }
    }

    .v-card--overlay & {
      z-index: 1;
    }
  }

  &--elevated {
    box-shadow: $box-shadow-lg;
    border-radius: $border-radius;
  }

  &--overlay {
    border-radius: $border-radius;
  }

  &--animate-from {
    transform: translateY(15px);
    opacity: 0;
  }

  &--animate-to {
    transition-property: opacity, transform;
    transition-duration: 400ms;
    transition-timing-function: ease-in-out;
    transform: translateY(0);
    opacity: 1;
  }

  &__overlay {
    position: absolute;
    overflow: hidden;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    pointer-events: none;
    right: 0;
    top: 0;
    width: 75%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.6s;

    &--show {
      opacity: 1;

      &--noSplit {
        opacity: 0.5;
        @include media-breakpoint-up(md) {
          width: 55% !important;
        }
      }
    }

    .v-card.flex-column & {
      right: auto;
      top: 0;
      left: 0;
      width: 100%;
      height: 80%;
      border-radius: $border-radius $border-radius 0 0;
    }

    .v-card.flex-row & {
      right: auto;
      top: 0;
      left: 0;
      width: 75%;
      height: 100%;
      border-radius: $border-radius-left;
    }

    .v-card.flex-row-reverse & {
      right: 0;
      top: 0;
      left: auto;
      width: 75%;
      height: 100%;
      border-radius: $border-radius-right;
    }

    .v-card.flex-md-column & {
      @include media-breakpoint-up(md) {
        right: auto;
        top: 0;
        left: 0;
        width: 100%;
        height: 80%;
        border-radius: $border-radius $border-radius 0 0;
      }
    }

    .v-card.flex-md-row & {
      @include media-breakpoint-up(md) {
        right: auto;
        top: 0;
        left: 0;
        width: 75%;
        height: 100%;
        border-radius: $border-radius-left;
      }
    }

    .v-card.flex-md-row-reverse & {
      @include media-breakpoint-up(md) {
        right: 0;
        top: 0;
        left: auto;
        width: 75%;
        height: 100%;
        border-radius: $border-radius-right;
      }
    }

    .v-card.flex-lg-column & {
      @include media-breakpoint-up(lg) {
        right: auto;
        top: 0;
        left: 0;
        width: 100%;
        height: 80%;
        border-radius: $border-radius $border-radius 0 0;
      }
    }

    .v-card.flex-lg-row & {
      @include media-breakpoint-up(lg) {
        right: auto;
        top: 0;
        left: 0;
        width: 75%;
        height: 100%;
        border-radius: $border-radius-left;
      }
    }

    .v-card.flex-lg-row-reverse & {
      @include media-breakpoint-up(lg) {
        right: 0;
        top: 0;
        left: auto;
        width: 75%;
        height: 100%;
        border-radius: $border-radius-right;
      }
    }

    &__color {
      &-1,
      &-2 {
        position: absolute;
        top: -1%;
        left: -1%;
        width: 102%;
        height: 102%;
        display: none;
      }

      .v-card.flex-column &-2 {
        display: block;
        transform: rotate(180deg) translateY(-1px);
      }

      .v-card.flex-row &-1 {
        display: block;
        transform: rotate(180deg) translateX(-1px);
      }

      .v-card.flex-row-reverse &-1 {
        display: block;
        transform: translateX(-1px);
      }

      @include media-breakpoint-up(md) {
        .v-card.flex-md-column &-1 {
          display: none;
        }
        .v-card.flex-md-column &-2 {
          display: block;
          transform: rotate(180deg) translateY(-1px);
        }
      }

      @include media-breakpoint-up(md) {
        .v-card.flex-md-row &-2 {
          display: none;
        }
        .v-card.flex-md-row &-1 {
          display: block;
          transform: rotate(180deg) translateX(-1px);
        }
      }

      @include media-breakpoint-up(md) {
        .v-card.flex-md-row-reverse &-2 {
          display: none;
        }
        .v-card.flex-md-row-reverse &-1 {
          display: block;
          transform: translateX(-1px);
        }
      }

      @include media-breakpoint-up(lg) {
        .v-card.flex-lg-column &-1 {
          display: none;
        }
        .v-card.flex-lg-column &-2 {
          display: block;
          transform: rotate(180deg) translateY(-1px);
        }
      }

      @include media-breakpoint-up(lg) {
        .v-card.flex-lg-row &-2 {
          display: none;
        }
        .v-card.flex-lg-row &-1 {
          display: block;
          transform: rotate(180deg) translateX(-1px);
        }
      }

      @include media-breakpoint-up(lg) {
        .v-card.flex-lg-row-reverse &-2 {
          display: none;
        }
        .v-card.flex-lg-row-reverse &-1 {
          display: block;
          transform: translateX(-1px);
        }
      }
    }
  }

  &__image--wrapper + &__content {
    .v-card.flex-column & {
      border-radius: $border-radius-bottom;
    }

    .v-card.flex-row & {
      border-radius: $border-radius-right;
    }

    .v-card.flex-row-reverse & {
      border-radius: $border-radius-left;
    }

    @include media-breakpoint-up(md) {
      .v-card.flex-md-column & {
        border-radius: $border-radius-bottom;
      }

      .v-card.flex-md-row & {
        border-radius: $border-radius-right;
      }

      .v-card.flex-md-row-reverse & {
        border-radius: $border-radius-left;
      }
    }

    @include media-breakpoint-up(lg) {
      .v-card.flex-lg-column & {
        border-radius: $border-radius-bottom;
      }

      .v-card.flex-lg-row & {
        border-radius: $border-radius-right;
      }

      .v-card.flex-lg-row-reverse & {
        border-radius: $border-radius-left;
      }
    }
  }

  &__image--wrapper {
    min-width: 50%;
    position: relative;
    overflow: hidden;
    border-radius: $border-radius;
    flex-grow: 0;

    &--noSplit {
      min-width: auto;
    }
    &__video-overlay {
      position: absolute;
      height: 100%;
      width: 100%;
      background-color: #00000090;
      text-align: center;
      top: 0;

      &:hover {
        background-color: #00000050;
      }

      &--no-overlay {
        background-color: transparent;

        &:hover {
          background-color: transparent;
        }
      }

      &__icon {
        pointer-events: none;
        position: relative;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .v-card.flex-column & {
      border-radius: $border-radius-top;
    }

    .v-card.flex-row & {
      border-radius: $border-radius-left;
    }

    .v-card.flex-row-reverse & {
      border-radius: $border-radius-right;
    }

    @include media-breakpoint-up(md) {
      .v-card.flex-md-column & {
        border-radius: $border-radius-top;
      }

      .v-card.flex-md-row & {
        border-radius: $border-radius-left;
      }

      .v-card.flex-md-row-reverse & {
        border-radius: $border-radius-right;
      }
    }

    @include media-breakpoint-up(md) {
      .v-card.flex-md-column & {
        border-radius: $border-radius-top;
      }

      .v-card.flex-md-row & {
        border-radius: $border-radius-left;
      }

      .v-card.flex-md-row-reverse & {
        border-radius: $border-radius-right;
      }
    }

    @include media-breakpoint-up(lg) {
      .v-card.flex-lg-column & {
        border-radius: $border-radius-top;
      }

      .v-card.flex-lg-row & {
        border-radius: $border-radius-left;
      }

      .v-card.flex-lg-row-reverse & {
        border-radius: $border-radius-right;
      }
    }
  }

  &__image {
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    height: 100%;
    opacity: 0;
    transition: opacity 0.6s;

    &__default {
      display: block;

      @include media-breakpoint-up(md) {
        @media (min-resolution: 2dppx) {
          display: none;
        }
      }
    }

    &__large {
      display: none;

      @include media-breakpoint-up(md) {
        @media (min-resolution: 2dppx) {
          display: block;
        }
      }
    }

    &--show {
      opacity: 1;
    }

    &::before {
      content: "";
      display: block;
      padding-top: 75%;
    }
  }
}

a.v-card:hover {
  box-shadow: $box-shadow-lg;
  border-radius: $border-radius;

  .v-card__content {
    background: #fff !important;
  }
}

@media print {
  .v-card {
    page-break-inside: avoid;
  }
}
</style>
